import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { QuestionProps } from '@/app/models/question'
import { useIntl } from 'react-intl'
import {
    Color,
    LuxButton,
    LuxDatePicker,
    LuxIcon,
    LuxTextInput,
    LUX_INPUT_TYPE,
} from '@lux/wardrobe-lib'
import { Checkbox } from 'primereact/checkbox'
import { RadioButton } from 'primereact/radiobutton'
import AddAttachment from '../AddAttachment/AddAttachment'
import { TaskProps } from '@/app/models/task'
import './Question.scss'
import {
    canExecuteJobTask,
    canExecuteQuestionTask,
    LayoutActivityType,
    SmartlyActionSubtypes,
} from '@Techedge/digitalportal-shared'
import { AttachmentProps } from '@/app/models/attachment'
import Attachment from '../Attachment/Attachment'
import { Markup } from 'interweave'
import { NEW_ACTIVITY_EXECUTION_NAMESPACE } from '@/app/redux/reducers/activity-execution'

const Question = (props: {
    question?: QuestionProps
    task?: TaskProps
    className?: string
    onUpload: (files: any[], q?: QuestionProps, t?: TaskProps) => void
    onUpdateAnswer: (a: string | string[], q?: QuestionProps, t?: TaskProps) => void
    isJobQuestion?: boolean
    language?: string
    userId: string
    projectDetails
}) => {
    const {
        question,
        task,
        className,
        onUpload,
        onUpdateAnswer,
        isJobQuestion,
        userId,
        projectDetails,
    } = props

    const componentClass = 'smart-modals-question'

    const intl = useIntl()

    const [inputValue, setInputValue] = useState('')
    const [dateValue, setDateValue] = useState(null)
    const [numericValue, setNumericValue] = useState('')
    const [isPickerVisible, setIsPickerVisible] = useState(false)

    useEffect(() => {
        if (question) {
            if (question?.questionType === 'text') {
                setInputValue(question.answer)
            }
            if (question?.questionType === 'date' && !!question.answer) {
                setDateValue(new Date(Number(question.answer)))
            }
            if (question?.questionType === 'numeric') {
                setNumericValue(question.answer)
            }
        }
        if (question?.questionType === 'date') {
            const pickerIcon = document
                .getElementsByClassName('smart-modals-question__picker')[0]
                .getElementsByClassName('p-datepicker-trigger')[0]
            pickerIcon.addEventListener('click', () =>
                setIsPickerVisible(!document.getElementsByClassName('execution-question-picker')[0])
            )
        }
        /**
         * Alert if clicked on outside of element
         */
        function handleClickOutside(event) {
            const pickerStart = document.getElementsByClassName('execution-question-picker')[0]
            if (!!pickerStart && !pickerStart.contains(event.target)) {
                setIsPickerVisible(false)
            }
        }
        // Bind the event listener
        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            // Unbind the event listener on clean up
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    const handleInputChange = (value) => {
        setInputValue(value)
    }
    const handleNumericChange = (value) => {
        setNumericValue(value)
    }

    const onUploadCustom = (payload: any) => {
        if (question && task) {
            onUpload(payload, question, task)
        } else if (question) {
            onUpload(payload, question)
        } else {
            onUpload(payload)
        }
    }

    const onUpdateAnswerCustom = (answer: string, isDisabled?: boolean) => {
        if (!isDisabled) {
            let newAnswer: string | string[] = answer
            if (question.questionType === 'multiChoice') {
                let toRemove = []
                newAnswer = question.answer ? [...(question as any).answer] : []
                if (!newAnswer.includes(answer)) {
                    newAnswer.push(answer)
                } else {
                    toRemove.push(answer)
                }
                newAnswer = newAnswer.filter((val) => !toRemove.includes(val))
            }
            if (question && task) {
                onUpdateAnswer(newAnswer, question, task)
            } else if (question) {
                onUpdateAnswer(newAnswer, question)
            } else {
                onUpdateAnswer(newAnswer)
            }
        }
    }

    const onFocus = () => {
        setIsPickerVisible(true)
    }
    const onVisibleChange = (e) => {}
    const onPickerChangeCallback = (value) => {
        setDateValue(value || null)
        onUpdateAnswerCustom(value ? new Date(value).getTime().toString() : null)
        setIsPickerVisible(false)
    }

    const renderAnswers = () => {
        const permission = isJobQuestion ? canExecuteJobTask : canExecuteQuestionTask
        let isDisabled = !(
            question.assignmentUserId?.includes(userId) ||
            permission({
                status: question.storeStatus,
                module: 'communication',
                type: 'communication',
                assignment: question.assignment,
            })
        )
        if (
            projectDetails.type !== LayoutActivityType.TASK &&
            projectDetails.type !== LayoutActivityType.SURVEY_TASK
        ) {
            isDisabled =
                !(
                    question.assignmentUserId?.includes(userId) ||
                    permission({
                        status: question.storeStatus,
                        module: 'communication',
                        type: 'communication',
                        assignment: question.assignment,
                    })
                ) || !question.hasAccepted
        }

        if (question?.questionType) {
            switch (question.questionType) {
                // singleChoice case
                case 'singleChoice':
                    if (question?.possibleAnswers?.length > 0) {
                        return question.possibleAnswers.map((answer: string, i: number) => {
                            return (
                                <div
                                    key={i}
                                    className={`${componentClass}__question__answers__answer`}>
                                    <RadioButton
                                        name={answer}
                                        value={answer}
                                        checked={question.answer === answer}
                                        onChange={() => onUpdateAnswerCustom(answer, isDisabled)}
                                        disabled={isDisabled}
                                    />
                                    <label
                                        htmlFor={answer}
                                        onClick={() => onUpdateAnswerCustom(answer, isDisabled)}>
                                        {answer}
                                    </label>
                                </div>
                            )
                        })
                    }
                    return intl.formatMessage({
                        id: 'modals.news_execution.action.questions.alert.no_possibleAnswers_defined',
                        defaultMessage: 'No possibleAnswers defined.',
                    })
                // multiChoice case
                case 'multiChoice':
                    if (question?.possibleAnswers?.length > 0) {
                        return question.possibleAnswers.map((answer: string, i: number) => {
                            return (
                                <div
                                    key={i}
                                    className={`${componentClass}__question__answers__answer`}>
                                    <Checkbox
                                        name={answer}
                                        value={answer}
                                        inputId={answer}
                                        checked={question?.answer?.includes(answer)}
                                        onChange={() => onUpdateAnswerCustom(answer, isDisabled)}
                                        disabled={isDisabled}
                                    />
                                    <label
                                        htmlFor={answer}
                                        onClick={() => onUpdateAnswerCustom(answer, isDisabled)}>
                                        {answer}
                                    </label>
                                </div>
                            )
                        })
                    }
                    return intl.formatMessage({
                        id: 'modals.news_execution.action.questions.alert.no_possibleAnswers_defined',
                        defaultMessage: 'No possibleAnswers defined.',
                    })
                // text case
                case 'text':
                    return (
                        <div className="inputWithSaveButton">
                            <LuxTextInput
                                value={inputValue}
                                onChangeCallback={(value: string) => handleInputChange(value)}
                                placeholder={intl.formatMessage({
                                    id: 'modals.news_execution.action.questions.input.text.placeholder',
                                    defaultMessage: 'Type your answer',
                                })}
                                disabled={isDisabled}
                            />
                            <LuxButton
                                variant="outlined"
                                onClick={() => onUpdateAnswerCustom(inputValue, isDisabled)}
                                text={intl.formatMessage({
                                    id: 'modals.new.activity.section.save',
                                    defaultMessage: 'Save',
                                })}
                                disabled={isDisabled}
                            />
                        </div>
                    )
                case 'yesNo':
                    return (
                        <>
                            <div className={`${componentClass}__question__answers__answer`}>
                                <RadioButton
                                    name={'yes'}
                                    value={'yes'}
                                    checked={question.answer === 'yes'}
                                    onChange={() => onUpdateAnswerCustom('yes', isDisabled)}
                                    disabled={isDisabled}
                                />
                                <label
                                    htmlFor={'yes'}
                                    onClick={() => onUpdateAnswerCustom('yes', isDisabled)}>
                                    {intl.formatMessage({
                                        id: 'modals.news_execution.action.questions.answer.yes',
                                        defaultMessage: 'Yes',
                                    })}
                                </label>
                            </div>
                            <div className={`${componentClass}__question__answers__answer`}>
                                <RadioButton
                                    name={'no'}
                                    value={'no'}
                                    checked={question.answer === 'no'}
                                    onChange={() => onUpdateAnswerCustom('no', isDisabled)}
                                    disabled={isDisabled}
                                />
                                <label
                                    htmlFor={'yes'}
                                    onClick={() => onUpdateAnswerCustom('no', isDisabled)}>
                                    {intl.formatMessage({
                                        id: 'modals.news_execution.action.questions.answer.no',
                                        defaultMessage: 'No',
                                    })}
                                </label>
                            </div>
                        </>
                    )
                case 'numeric':
                    return (
                        <div className="inputWithSaveButton">
                            <LuxTextInput
                                value={numericValue}
                                type={LUX_INPUT_TYPE.number}
                                onChangeCallback={(value: string) => handleNumericChange(value)}
                                placeholder={intl.formatMessage({
                                    id: 'modals.news_execution.action.questions.input.text.placeholder',
                                    defaultMessage: 'Type your answer',
                                })}
                                disabled={isDisabled}
                            />
                            <LuxButton
                                variant="outlined"
                                onClick={() => onUpdateAnswerCustom(numericValue, isDisabled)}
                                text={intl.formatMessage({
                                    id: 'modals.new.activity.section.save',
                                    defaultMessage: 'Save',
                                })}
                                disabled={isDisabled}
                            />
                        </div>
                    )
                case 'date':
                    return (
                        <LuxDatePicker
                            containerClasses={`${componentClass}__picker`}
                            intl={intl}
                            locale={props.language?.split('-')[0] || 'en'}
                            additionalProps={{
                                panelClassName:
                                    'smart-wardrobe-datepicker execution-question-picker',
                            }}
                            value={dateValue}
                            onChangeCallback={onPickerChangeCallback}
                            showCustom
                            visible={isPickerVisible}
                            onFocus={onFocus}
                            onVisibleChange={onVisibleChange}
                            style={{ backgroundColor: Color.WHITE }}
                            disabled={isDisabled}
                        />
                    )
                default:
                    return (
                        <div className="no_questionType">
                            {intl.formatMessage({
                                id: 'modals.news_execution.action.questions.alert.no_questionType_defined',
                                defaultMessage: 'No questionType defined.',
                            })}
                        </div>
                    )
            }
        }
        return <></>
    }

    return (
        <div className={`${componentClass} ${className ? className : ''}`}>
            <div className={`${componentClass}__question__question`}>
                <div>
                    <Markup content={question.questionTitle} />
                </div>
            </div>
            {question.attachments && question.attachments.length > 0 && (
                <div className={`${componentClass}__attachments`}>
                    {question.attachments.map((a: AttachmentProps, i: number) => {
                        return (
                            <div key={i}>
                                {a && (
                                    <Attachment
                                        key={i}
                                        fileUrl={a?.url}
                                        fileName={a?.name}
                                        fileExtension={a?.ext}
                                        showDownloadIcon
                                        hideEyeIcon
                                    />
                                )}
                            </div>
                        )
                    })}
                </div>
            )}
            <div className={`${componentClass}__question__answers`}>{renderAnswers()}</div>
            <div className={`${componentClass}__question__attachment`}>
                {question.attachmentsRequired && (
                    <div className={`${componentClass}__question__attachment__alert`}>
                        <LuxIcon
                            icon="info"
                            color={question.status === 'not_completed' ? Color.RED : Color.BLACK}
                        />
                        <p className={question.status}>
                            {intl.formatMessage({
                                id: 'modals.news_execution.action.questions.alert',
                                defaultMessage: 'This question has mandatory attachment.',
                            })}
                        </p>
                    </div>
                )}
                <div className={`${componentClass}__question__attachment__upload`}>
                    <AddAttachment
                        id={`questions-${Math.random() * 100}`}
                        onUpload={(payload) => onUploadCustom(payload)}
                        form={{
                            attachmentList: { value: question.answerAttachments },
                        }}
                        showSplitFile={false}
                        disabled={
                            !(question.answer || question.answers?.length) ||
                            !(
                                question.assignmentUserId?.includes(userId) ||
                                canExecuteQuestionTask({
                                    status: question.storeStatus,
                                    module: 'communication',
                                    type: 'communication',
                                    assignment: question.assignment,
                                }) ||
                                (isJobQuestion &&
                                    canExecuteJobTask({
                                        status: question.storeStatus,
                                        module: 'communication',
                                        type: 'communication',
                                        assignment: question.assignment,
                                    }))
                            )
                        }
                    />
                </div>
            </div>
        </div>
    )
}

const mapStateToProps = (state: any) => ({
    language: state.userReducer.user.language,
    userId: state.userReducer.user.user_id,
    projectDetails: state[NEW_ACTIVITY_EXECUTION_NAMESPACE].projectDetails,
})

export default connect(mapStateToProps, () => ({}))(Question)
